"use client";

import * as React from "react";

import { isNodeSelection, type Editor } from "@tiptap/react";

// --- Icons ---
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faList,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
// --- UI Primitives ---
import {
  UnifiedButton,
  UnifiedButtonProps,
} from "@/components/tiptap-ui/unified-button";
// --- Tiptap UI ---
import {
  canToggleList,
  isListActive,
  listOptions,
  type ListType,
} from "@/components/tiptap-ui/list-button/list-button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { isNodeInSchema } from "@/lib/tiptap-utils";
import { cn } from "../../../lib/utils";

interface ListDropdownMenuProps extends Omit<UnifiedButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor;
  /**
   * The list types to display in the dropdown.
   */
  types?: ListType[];
  /**
   * Whether the dropdown should be hidden when no list types are available
   * @default false
   */
  hideWhenUnavailable?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

function canToggleAnyList(
  editor: Editor | null,
  listTypes: ListType[],
): boolean {
  if (!editor) return false;
  return listTypes.some((type) => canToggleList(editor, type));
}

function isAnyListActive(
  editor: Editor | null,
  listTypes: ListType[],
): boolean {
  if (!editor) return false;
  return listTypes.some((type) => isListActive(editor, type));
}

function getFilteredListOptions(
  availableTypes: ListType[],
): typeof listOptions {
  return listOptions.filter((option) => availableTypes.includes(option.type));
}

function shouldShowListDropdown(params: {
  editor: Editor | null;
  listTypes: ListType[];
  hideWhenUnavailable: boolean;
  listInSchema: boolean;
  canToggleAny: boolean;
}): boolean {
  const { editor, hideWhenUnavailable, listInSchema, canToggleAny } = params;

  if (!listInSchema) {
    return false;
  }

  if (hideWhenUnavailable) {
    if (isNodeSelection(editor?.state.selection) && !canToggleAny) {
      return false;
    }
  }

  return true;
}

function useListDropdownState(
  editor: Editor | null,
  availableTypes: ListType[],
) {
  const [isOpen, setIsOpen] = React.useState(false);

  const listInSchema = availableTypes.some((type) =>
    isNodeInSchema(type, editor),
  );

  const filteredLists = React.useMemo(
    () => getFilteredListOptions(availableTypes),
    [availableTypes],
  );

  const canToggleAny = canToggleAnyList(editor, availableTypes);
  const isAnyActive = isAnyListActive(editor, availableTypes);

  const handleOpenChange = React.useCallback(
    (open: boolean, callback?: (isOpen: boolean) => void) => {
      setIsOpen(open);
      callback?.(open);
    },
    [],
  );

  return {
    isOpen,
    setIsOpen,
    listInSchema,
    filteredLists,
    canToggleAny,
    isAnyActive,
    handleOpenChange,
  };
}

function useActiveListIcon(
  editor: Editor | null,
  filteredLists: typeof listOptions,
) {
  return React.useCallback(() => {
    const activeOption = filteredLists.find((option) =>
      isListActive(editor, option.type),
    );

    const iconDefinition = activeOption ? activeOption.icon : faList;
    return (
      <FontAwesomeIcon icon={iconDefinition} className="tiptap-button-icon" />
    );
  }, [editor, filteredLists]);
}

export function ListDropdownMenu({
  editor: providedEditor,
  types = ["bulletList", "orderedList", "taskList"],
  hideWhenUnavailable = false,
  onOpenChange,
  ...props
}: ListDropdownMenuProps) {
  const editor = useTiptapEditor(providedEditor);

  const {
    isOpen,
    listInSchema,
    filteredLists,
    canToggleAny,
    isAnyActive,
    handleOpenChange,
  } = useListDropdownState(editor, types);

  const getActiveIcon = useActiveListIcon(editor, filteredLists);

  const show = React.useMemo(() => {
    return shouldShowListDropdown({
      editor,
      listTypes: types,
      hideWhenUnavailable,
      listInSchema,
      canToggleAny,
    });
  }, [editor, types, hideWhenUnavailable, listInSchema, canToggleAny]);

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => handleOpenChange(open, onOpenChange),
    [handleOpenChange, onOpenChange],
  );

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <DropdownMenuTrigger asChild>
        <UnifiedButton
          type="button"
          data-style="ghost"
          data-active-state={isAnyActive ? "on" : "off"}
          role="button"
          tabIndex={-1}
          aria-label="List options"
          tooltip="List"
          {...props}
        >
          {getActiveIcon()}
          <FontAwesomeIcon
            icon={faChevronDown}
            className="tiptap-button-dropdown-small"
          />
        </UnifiedButton>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuGroup>
          {filteredLists.map((option) => {
            const isActive = isListActive(editor, option.type);
            const iconDefinition = option.icon;
            return (
              <DropdownMenuItem
                key={option.type}
                className={cn({
                  "font-bold": isActive,
                })}
                onClick={() => {
                  switch (option.type) {
                    case "bulletList":
                      editor.chain().focus().toggleBulletList().run();
                      break;
                    case "orderedList":
                      editor.chain().focus().toggleOrderedList().run();
                      break;
                    case "taskList":
                      editor
                        .chain()
                        .focus()
                        .toggleList("taskList", "taskItem")
                        .run();
                      break;
                  }
                }}
              >
                <FontAwesomeIcon icon={iconDefinition} className="mr-2" />
                <span>{option.label}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
